//Libs
import {
  CellContextMenuEvent,
  ColDef,
  GetQuickFilterTextParams,
  GridApi,
  ICellRendererParams,
  ITooltipParams,
  RowClickedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import React from "react";
import isEqual from "lodash/isEqual";

//Local
import { ExistingTask } from "../../models/task";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import { getReadableCraftType } from "../../models/craft-types";
import { getReadableTaskType } from "../../models/task-types";
import { getReadableTaskStatus } from "../../models/task-status";
import * as strings from "../../strings";
import SearchBox from "../SearchBox";
import TaskStatusCustomFilter from "../tables/TaskStatusCustomFilter";
import { WORK_RECORD_AND_TASKS_URL } from "../../urls";
import { getTaskStatusPillClasses } from "../../assets/js/tasks";
import TaskTypeCustomFilter from "../tables/TaskTypeCustomFilter";

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";

interface CustomerTaskListTableProps {
  tasksTableData: ExistingTask[];
  renderIconCell: (params: ICellRendererParams) => JSX.Element;
  goToWorkRecordAndTasksPage: (
    craftRecordID: ExistingTask["craftRecordID"],
  ) => void;
  children: {
    DropdownSelectionTasks: React.ReactNode;
  };
}

const commonColProps = {
  width: 150,
  flex: 1,
  resizable: true,
  filter: true,
  sortable: true,
  headerClass:
    "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
};

const CustomerTaskListTable = React.memo(
  ({
    tasksTableData,
    renderIconCell,
    goToWorkRecordAndTasksPage,
    ...props
  }: CustomerTaskListTableProps) => {
    const [gridReady, setGridReady] = useState(false);
    const gridRef = useRef<AgGridReact>(null);

    const onGridReady = useCallback(() => {
      setGridReady(true);
    }, []);

    useLayoutEffect(() => {
      if (gridRef.current && gridReady) {
        const api: GridApi = gridRef.current.api;
        api?.sizeColumnsToFit();
      }
    }, [gridReady]);

    const onFilterTextBoxChanged = useCallback(() => {
      gridRef.current?.api?.setGridOption(
        "quickFilterText",
        (document.getElementById("filter-text-box") as HTMLInputElement).value,
      );
    }, []);

    const taskTableColumnsDefs: ColDef[] = [
      {
        field: "title",
        headerName: "Title",
        tooltipValueGetter: (params) => params.data.title,
      },
      {
        field: "taskStatus",
        headerName: "Task Status",
        filter: TaskStatusCustomFilter,
        cellRenderer: readableTaskStatus,
        tooltipValueGetter: renderTaskStatus,
        valueFormatter: renderTaskStatus,
        getQuickFilterText: renderTaskStatus,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "craftType",
        headerName: "Work Type",
        valueGetter: renderCraftType,
        valueFormatter: renderCraftType,
      },
      {
        field: "taskType",
        headerName: "Task Type",
        filter: TaskTypeCustomFilter,
        cellRenderer: renderTaskType,
        valueFormatter: renderTaskType,
      },
      {
        headerName: "Schedule Flags",
        cellRenderer: renderScheduleFlags,
        valueFormatter: (params: ValueFormatterParams) => params.value,
        valueGetter: (params) => getScheduleFlagsValue(params),
        tooltipValueGetter: (params) => getScheduleFlagsValue(params),
        comparator: (valueA, valueB) => {
          if (valueA === valueB) return 0;
          return valueA > valueB ? 1 : -1;
        },
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "timestampScheduled",
        headerName: "Scheduled",
        valueFormatter: getTimestampScheduled,
        cellRenderer: getTimestampScheduled,
      },
      {
        cellRenderer: renderIconCell,
        colId: "actionColumnId",
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        sortable: false,
        flex: 0,
      },
    ];

    return (
      <div>
        <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:items-end sm:justify-between">
          <SearchBox
            onInput={onFilterTextBoxChanged}
            widthClasses="grow w-full sm:w-auto sm:max-w-sm"
          />
          {props.children.DropdownSelectionTasks}
        </div>
        <div className="ag-theme-alpine h-[300px]">
          <AgGridReact
            reactiveCustomComponents
            ref={gridRef}
            onGridReady={onGridReady}
            defaultColDef={commonColProps}
            className="mt-5 shadow"
            rowData={tasksTableData.sort((a, b) => {
              // if timestampScheduled is null, replace with timestampCreated, otherwise sort by timestampScheduled
              const aTimestamp = a.timestampScheduled ?? a.timestampCreated;
              const bTimestamp = b.timestampScheduled ?? b.timestampCreated;
              return aTimestamp < bTimestamp ? 1 : -1;
            })}
            animateRows={true}
            rowSelection="single"
            columnDefs={taskTableColumnsDefs}
            onRowClicked={(event: RowClickedEvent) => {
              goToWorkRecordAndTasksPage(
                event.data.craftRecordID.split("/")[3],
              );
            }}
            onCellContextMenu={(event: CellContextMenuEvent<any, any>) => {
              const craftRecordID = event.data.craftRecordID.split("/")[3];
              const url = `${WORK_RECORD_AND_TASKS_URL}/${craftRecordID}`;
              return window.open(url, "_blank");
            }}
          />
        </div>
      </div>
    );
  },
  (previous, next) => {
    const isTaskListTheSame = isEqual(
      previous.tasksTableData,
      next.tasksTableData,
    );

    return isTaskListTheSame;
  },
);

export default CustomerTaskListTable;

export function getScheduleFlagsValue(
  params: ITooltipParams | ValueGetterParams,
): string {
  if (params.data.urgent) {
    return "urgent";
  } else if (params.data.nextOpportunity) {
    return "next opportunity";
  } else {
    return "";
  }
}

function getTimestampScheduled(
  params: ICellRendererParams | ValueFormatterParams,
): string {
  return params.value != null
    ? convertToReadableTimestamp(params.value)
    : "Not scheduled";
}

function renderTaskType(
  params: ICellRendererParams | ValueFormatterParams,
): string {
  return getReadableTaskType(params.value);
}

function renderCraftType(
  params: ValueFormatterParams | ValueGetterParams,
): string {
  return getReadableCraftType(params.data.craftType);
}

function renderTaskStatus(
  params: ValueFormatterParams | ITooltipParams | GetQuickFilterTextParams,
): string {
  return getReadableTaskStatus(params.data.taskStatus);
}

function renderScheduleFlags(params: ICellRendererParams): JSX.Element | null {
  if (params.data.urgent === true) {
    return (
      <div className="flex items-center">
        <div className="m-1 rounded-full bg-orange-400 px-2.5 pb-1 pt-0.5 text-xs font-medium text-white">
          {strings.URGENT}
        </div>
      </div>
    );
  } else if (params.data.nextOpportunity === true) {
    return (
      <div className="flex items-center">
        <div className="m-1 rounded-full bg-green-400 px-2.5 pb-1 pt-0.5 text-xs font-medium text-white">
          {strings.NEXT_OPPORTUNITY}
        </div>
      </div>
    );
  } else {
    return null;
  }
}

function readableTaskStatus(params: ICellRendererParams): JSX.Element {
  return (
    <div className="flex items-center">
      <div
        className={`rounded-full px-2 py-1 text-xs font-medium ${getTaskStatusPillClasses(params.data.taskStatus)}`}
      >
        {getReadableTaskStatus(params.data.taskStatus)}
      </div>
    </div>
  );
}
